// Function definitions and implementations for OpenAI Function Calling
import { DIRECTUS_DATASETS_ENDPOINT, LOCAL_WORKER_API_BASE } from '../../api';
import axios from 'axios';
// Logger removed - using inline conditions for production builds

// Static available functions definitions (existing ones)
export const staticAvailableFunctions = [
  {
    name: "get_weather",
    description: "Get current weather information for a specific location using coordinates",
    parameters: {
      type: "object",
      properties: {
        latitude: {
          type: "number",
          description: "Latitude coordinate (e.g., 52.2297 for Warsaw)"
        },
        longitude: {
          type: "number", 
          description: "Longitude coordinate (e.g., 21.0122 for Warsaw)"
        },
        location_name: {
          type: "string",
          description: "Human readable location name (e.g., 'Warsaw, Poland')"
        }
      },
      required: ["latitude", "longitude"]
    }
  },
  {
    name: "search_ncbi_articles",
    description: "Search scientific articles in NCBI PubMed Central database",
    parameters: {
      type: "object",
      properties: {
        query: {
          type: "string",
          description: "Search query for medical/scientific literature (e.g., 'diabetes treatment', 'COVID-19 vaccines')"
        },
        max_results: {
          type: "number",
          description: "Maximum number of results to return (default: 5, max: 20)"
        },
        search_field: {
          type: "string",
          enum: ["all", "title", "abstract", "author"],
          description: "Field to search in (default: all)"
        }
      },
      required: ["query"]
    }
  }
];

// Cache for dynamic functions
let dynamicFunctionsCache = [];
let lastCacheUpdate = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Function to load API datasets and convert them to function definitions
export const loadApiDatasetFunctions = async () => {
  process.env.NODE_ENV === "development" && console.log('🔧 loadApiDatasetFunctions called');
  const now = Date.now();
  
  // Return cached functions if cache is still valid
  if (dynamicFunctionsCache.length > 0 && (now - lastCacheUpdate) < CACHE_DURATION) {
    process.env.NODE_ENV === "development" && console.log('🔄 Using cached API dataset functions:', dynamicFunctionsCache.length, 'functions');
    process.env.NODE_ENV === "development" && console.log('📦 Cached functions:', dynamicFunctionsCache.map(f => f.name));
    return dynamicFunctionsCache;
  }

  process.env.NODE_ENV === "development" && console.log('📡 Loading API dataset functions from Directus... (cache expired or empty)');
  
  try {
    const token = localStorage.getItem('directus_token');
    const userId = localStorage.getItem('user_id');
    
    if (!token || !userId) {
      process.env.NODE_ENV === "development" && console.warn('⚠️ No token or user ID available for loading API datasets');
      return [];
    }

    // Fetch API datasets from Directus
    process.env.NODE_ENV === "development" && console.log('📡 Making request to:', DIRECTUS_DATASETS_ENDPOINT);
    process.env.NODE_ENV === "development" && console.log('🔑 Using userId:', userId);
    process.env.NODE_ENV === "development" && console.log('🔍 Filter params:', {
      user_created: { _eq: userId },
      type: { _eq: 'external_api' }
    });
    
    const response = await axios.get(DIRECTUS_DATASETS_ENDPOINT, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        fields: ['id', 'name', 'api_config', 'type', 'dataset_type'],
        filter: {
          user_created: { _eq: userId },
          type: { _eq: 'external_api' }
        }
      }
    });

    process.env.NODE_ENV === "development" && console.log('📥 Raw response from Directus:', response.data);
    const apiDatasets = response.data.data || [];
    process.env.NODE_ENV === "development" && console.log(`📊 Found ${apiDatasets.length} API datasets:`, apiDatasets);

    // Convert each API dataset to a function definition
    process.env.NODE_ENV === "development" && console.log('🔄 Processing each dataset...');
    const dynamicFunctions = apiDatasets.map((dataset, index) => {
      process.env.NODE_ENV === "development" && console.log(`🔄 Processing dataset ${index + 1}/${apiDatasets.length}:`, dataset);
      
      try {
        process.env.NODE_ENV === "development" && console.log('📋 Raw api_config:', dataset.api_config);
        // api_config is already an object in Directus, no need to parse
        const apiConfig = dataset.api_config;
        process.env.NODE_ENV === "development" && console.log('📋 API config object:', apiConfig);
        
        // Generate function definition from API config
        const functionDef = {
          name: apiConfig.functionName || generateFunctionNameFromDataset(dataset.name),
          description: apiConfig.description || `Call ${dataset.name} API endpoint`,
          datasetId: dataset.id, // Store dataset ID for execution
          parameters: {
            type: "object",
            properties: {},
            required: []
          }
        };

        // Add search_query parameter to all dynamic functions
        functionDef.parameters.properties.search_query = {
          type: "string",
          description: "Optional search query to filter results from the API response. If provided, the function will search through the returned data and only return matching items."
        };

        // Convert API parameters to function parameters
        if (apiConfig.parameters && Array.isArray(apiConfig.parameters)) {
          process.env.NODE_ENV === "development" && console.log('📋 Processing parameters:', apiConfig.parameters);
          apiConfig.parameters.forEach(param => {
            if (param.name) {
              functionDef.parameters.properties[param.name] = {
                type: "string", // Default to string, could be enhanced
                description: param.description || `${param.name} parameter for ${dataset.name} API`
              };
              
              if (param.required) {
                functionDef.parameters.required.push(param.name);
              }
            }
          });
        }

        process.env.NODE_ENV === "development" && console.log(`✅ Generated function definition for dataset: ${dataset.name}`, functionDef);
        return functionDef;
      } catch (error) {
        process.env.NODE_ENV === "development" && console.error(`❌ Error processing dataset ${dataset.name}:`, error);
        process.env.NODE_ENV === "development" && console.error('❌ Error details:', error.stack);
        return null;
      }
    }).filter(func => func !== null);
    
    process.env.NODE_ENV === "development" && console.log('🎯 Filtered dynamic functions:', dynamicFunctions);

    // Remove duplicates by function name (keep first occurrence)
    const seenNames = new Set();
    const uniqueFunctions = dynamicFunctions.filter(func => {
      if (seenNames.has(func.name)) {
        process.env.NODE_ENV === "development" && console.warn(`⚠️ Duplicate function name detected: ${func.name} - skipping duplicate`);
        return false;
      }
      seenNames.add(func.name);
      return true;
    });

    process.env.NODE_ENV === "development" && console.log(`🎯 After deduplication: ${uniqueFunctions.length} unique functions`);

    // Update cache
    dynamicFunctionsCache = uniqueFunctions;
    lastCacheUpdate = now;
    
    process.env.NODE_ENV === "development" && console.log(`🎯 Successfully loaded ${uniqueFunctions.length} API dataset functions`);
    return uniqueFunctions;
    
  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('❌ Error loading API dataset functions:', error);
    process.env.NODE_ENV === "development" && console.error('❌ Error response:', error.response?.data);
    process.env.NODE_ENV === "development" && console.error('❌ Error status:', error.response?.status);
    process.env.NODE_ENV === "development" && console.error('❌ Error config:', error.config);
    return [];
  }
};

// Helper function to generate valid function names
const generateFunctionNameFromDataset = (datasetName) => {
  return datasetName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters except spaces
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single
    .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
};

// Helper function to search through API data
const searchInApiData = (data, searchQuery) => {
  process.env.NODE_ENV === "development" && console.log(`🔍 Starting search for "${searchQuery}" in data type: ${typeof data}`);
  
  const results = {
    matches: [],
    total_matches: 0,
    searched_items: 0
  };

  if (!data || !searchQuery) {
    process.env.NODE_ENV === "development" && console.log('❌ No data or search query provided');
    return results;
  }

  const query = searchQuery.toLowerCase().trim();
  process.env.NODE_ENV === "development" && console.log(`🔍 Normalized search query: "${query}"`);

  // Function to search in text content
  const searchInText = (text, source, item) => {
    if (typeof text !== 'string') return false;
    
    const lowerText = text.toLowerCase();
    if (lowerText.includes(query)) {
      process.env.NODE_ENV === "development" && console.log(`✅ Found match in ${source}: "${text.substring(0, 100)}..."`);
      return true;
    }
    return false;
  };

  // Function to recursively search in object/array
  const searchInItem = (item, index = 0) => {
    results.searched_items++;
    let hasMatch = false;
    let matchDetails = {
      item_index: index,
      matches_found: [],
      content: {}
    };

    if (Array.isArray(item)) {
      process.env.NODE_ENV === "development" && console.log(`🔍 Searching in array with ${item.length} items`);
      item.forEach((subItem, subIndex) => {
        const subResult = searchInItem(subItem, subIndex);
        if (subResult) {
          hasMatch = true;
          matchDetails.matches_found.push(`Array item ${subIndex}`);
        }
      });
    } else if (typeof item === 'object' && item !== null) {
      process.env.NODE_ENV === "development" && console.log(`🔍 Searching in object with keys: ${Object.keys(item).slice(0, 5).join(', ')}`);
      
      // Search in specific fields that are likely to contain text content
      const searchFields = ['title', 'content', 'rendered', 'excerpt', 'name', 'description', 'text', 'body', 'summary'];
      
      for (const [key, value] of Object.entries(item)) {
        // Only search in relevant fields
        if (!searchFields.includes(key) && typeof value !== 'string') {
          continue;
        }
        if (typeof value === 'string') {
          if (searchInText(value, `field "${key}"`, item)) {
            hasMatch = true;
            matchDetails.matches_found.push(key);
            matchDetails.content[key] = value.length > 200 ? 
              value.substring(0, 200) + '...' : value;
          }
        } else if (typeof value === 'object' && value !== null) {
          // For nested objects (like title.rendered, content.rendered)
          if (value.rendered && typeof value.rendered === 'string') {
            if (searchInText(value.rendered, `field "${key}.rendered"`, item)) {
              hasMatch = true;
              matchDetails.matches_found.push(`${key}.rendered`);
              matchDetails.content[key] = value.rendered.length > 200 ? 
                value.rendered.substring(0, 200) + '...' : value.rendered;
            }
          }
        }
      }
      
      // If it's a WordPress post, also check excerpt
      if (item.excerpt && item.excerpt.rendered) {
        if (searchInText(item.excerpt.rendered, 'excerpt', item)) {
          hasMatch = true;
          matchDetails.matches_found.push('excerpt');
          matchDetails.content.excerpt = item.excerpt.rendered.length > 200 ? 
            item.excerpt.rendered.substring(0, 200) + '...' : item.excerpt.rendered;
        }
      }
    } else if (typeof item === 'string') {
      if (searchInText(item, 'string value', item)) {
        hasMatch = true;
        matchDetails.matches_found.push('string_content');
        matchDetails.content.text = item.length > 200 ? item.substring(0, 200) + '...' : item;
      }
    }

    if (hasMatch) {
      results.total_matches++;
      results.matches.push(matchDetails);
      process.env.NODE_ENV === "development" && console.log(`✅ Added match for item ${index}, fields: ${matchDetails.matches_found.join(', ')}`);
    }

    return hasMatch;
  };

  // Start searching
  if (Array.isArray(data)) {
    process.env.NODE_ENV === "development" && console.log(`🔍 Searching in array with ${data.length} items`);
    data.forEach((item, index) => {
      searchInItem(item, index);
    });
  } else {
    process.env.NODE_ENV === "development" && console.log(`🔍 Searching in single object`);
    searchInItem(data, 0);
  }

  process.env.NODE_ENV === "development" && console.log(`🔍 Search complete: ${results.total_matches} matches found in ${results.searched_items} items`);
  return results;
};

// Helper function to extract source information from API response
const extractSourceInfo = (data, apiConfig, requestUrl) => {
  const baseUrl = apiConfig.baseUrl || '';
  const domain = baseUrl.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  
  return {
    api_endpoint: requestUrl,
    domain: domain,
    base_url: baseUrl,
    api_name: apiConfig.name || 'External API',
    timestamp: new Date().toISOString()
  };
};

// Helper function to extract source URLs from individual matches
const extractMatchSources = (match, originalData, apiConfig) => {
  const sources = [];
  
  if (Array.isArray(originalData) && match.item_index < originalData.length) {
    const item = originalData[match.item_index];
    
    // For WordPress posts (common structure)
    if (item.link) {
      sources.push({
        url: item.link,
        title: item.title?.rendered || item.title || 'View Article',
        type: 'article'
      });
    }
    
    // For other API structures with URL fields
    const urlFields = ['url', 'permalink', 'href', 'link', 'web_url', 'source_url'];
    urlFields.forEach(field => {
      if (item[field] && typeof item[field] === 'string' && item[field].startsWith('http')) {
        sources.push({
          url: item[field],
          title: item.title?.rendered || item.title || item.name || 'View Source',
          type: 'source'
        });
      }
    });
    
    // If no direct URL found, try to construct one
    if (sources.length === 0 && apiConfig.baseUrl) {
      if (item.slug) {
        sources.push({
          url: `${apiConfig.baseUrl}/${item.slug}`,
          title: item.title?.rendered || item.title || item.name || 'View Item',
          type: 'constructed'
        });
      } else if (item.id) {
        sources.push({
          url: `${apiConfig.baseUrl}/?p=${item.id}`,
          title: item.title?.rendered || item.title || item.name || 'View Item',
          type: 'constructed'
        });
      }
    }
  }
  
  return sources;
};

// Execute API dataset function call
// Uses backend proxy to bypass CORS/CSP - works with panel.dailyeffect.pl, a.webratings, etc.
const executeApiDatasetFunction = async (functionName, parameters, datasetId) => {
  process.env.NODE_ENV === "development" && console.log(`🚀 Executing API dataset function: ${functionName} with dataset ID: ${datasetId}`);
  process.env.NODE_ENV === "development" && console.log(`🔍 Parameters received:`, parameters);
  
  try {
    const token = localStorage.getItem('directus_token');
    const isLocalhost = window.location.hostname === 'localhost';
    const proxyUrl = isLocalhost
      ? `${LOCAL_WORKER_API_BASE}/proxy-external-api`
      : 'https://api.prosecaptain.com/workers/api/proxy-external-api';

    const proxyResponse = await axios.post(proxyUrl, {
      datasetId,
      parameters: parameters || {}
    }, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      timeout: 30000
    });

    if (!proxyResponse.data?.success) {
      throw new Error(proxyResponse.data?.error || 'Proxy request failed');
    }

    const apiResponse = {
      data: proxyResponse.data.data,
      status: proxyResponse.data.status
    };

    // Fetch api_config for extractSourceInfo
    let apiConfig = {};
    try {
      const configRes = await axios.get(`${DIRECTUS_DATASETS_ENDPOINT}/${datasetId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { fields: ['api_config'] }
      });
      apiConfig = configRes.data?.data?.api_config || {};
    } catch (e) {
      process.env.NODE_ENV === "development" && console.warn('Could not fetch api_config for source info');
    }
    const requestUrl = `${apiConfig.baseUrl || ''}${apiConfig.endpoint || ''}`;
    const headers = {
      'Content-Type': 'application/json'
    };

    // Add custom headers
    if (apiConfig.headers) {
      apiConfig.headers.forEach(header => {
        if (header.name && header.value) {
          headers[header.name] = header.value;
        }
      });
    }

    // Add authentication headers
    if (apiConfig.auth) {
      switch (apiConfig.auth.type) {
        case 'bearer':
          if (apiConfig.auth.token && apiConfig.auth.token !== '[HIDDEN]') {
            headers['Authorization'] = `Bearer ${apiConfig.auth.token}`;
          } else if (apiConfig.auth.token === '[HIDDEN]') {
            // In production, this should retrieve from a secure credential store
            process.env.NODE_ENV === "development" && console.warn('⚠️ Bearer token is hidden. In production, implement secure credential retrieval.');
            // TODO: Implement secure credential retrieval
            // headers['Authorization'] = `Bearer ${await getSecureCredential(datasetId, 'bearer_token')}`;
          }
          break;
        case 'basic':
          if (apiConfig.auth.username && apiConfig.auth.password && apiConfig.auth.password !== '[HIDDEN]') {
            const credentials = btoa(`${apiConfig.auth.username}:${apiConfig.auth.password}`);
            headers['Authorization'] = `Basic ${credentials}`;
          } else if (apiConfig.auth.password === '[HIDDEN]') {
            process.env.NODE_ENV === "development" && console.warn('⚠️ Basic auth password is hidden. In production, implement secure credential retrieval.');
            // TODO: Implement secure credential retrieval
          }
          break;
        case 'api-key':
          if (apiConfig.auth.apiKey && apiConfig.auth.apiKeyHeader) {
            headers[apiConfig.auth.apiKeyHeader] = apiConfig.auth.apiKey;
            process.env.NODE_ENV === "development" && console.log('✅ Added API key header:', apiConfig.auth.apiKeyHeader);
          } else {
            process.env.NODE_ENV === "development" && console.warn('⚠️ API key or header name is missing');
          }
          break;
        default:
          // No auth or unknown auth type
          break;
      }
    }

    process.env.NODE_ENV === "development" && console.log('📡 Making API request:', {
      method: apiConfig.method,
      url: requestUrl,
      headers: Object.keys(headers)
    });

    // apiResponse already set from backend proxy above
    const usedProxy = true;

    // Extract search query from parameters if provided
    const searchQuery = parameters?.search_query || parameters?.query;
    let processedData = apiResponse.data;
    let searchResults = null;

    // Extract source information from API response
    const sourceInfo = extractSourceInfo(apiResponse.data, apiConfig, requestUrl);
    
    // If search query is provided, filter/search the data
    if (searchQuery) {
      process.env.NODE_ENV === "development" && console.log(`🔍 Searching for: "${searchQuery}" in API response`);
      searchResults = searchInApiData(apiResponse.data, searchQuery);
      process.env.NODE_ENV === "development" && console.log(`🔍 Search found ${searchResults.total_matches} matches in ${searchResults.searched_items} items`);
      
      // If we found specific matches, use filtered data
      if (searchResults.matches.length > 0) {
        // Add source info to each match
        const matchesWithSources = searchResults.matches.slice(0, 10).map(match => ({
          ...match,
          sources: extractMatchSources(match, apiResponse.data, apiConfig)
        }));
        
        processedData = {
          search_query: searchQuery,
          total_matches: searchResults.total_matches,
          searched_items: searchResults.searched_items,
          matches: matchesWithSources,
          raw_data_sample: Array.isArray(apiResponse.data) ? 
            `Full dataset contains ${apiResponse.data.length} items` : 
            'Full dataset available',
          api_source: sourceInfo
        };
      }
    } else {
      // For non-search results, add source info to the main data
      processedData = {
        ...apiResponse.data,
        api_source: sourceInfo
      };
    }

    return {
      success: true,
      data: processedData,
      status: apiResponse.status,
      function_name: functionName,
      dataset_id: datasetId,
      request_url: requestUrl.replace(/([?&]api[_-]?key=)[^&]+/gi, '$1[HIDDEN]'), // Hide API keys in logs
      data_source: `Custom API: ${functionName}${usedProxy ? ' (via proxy)' : ''}`,
      used_proxy: usedProxy,
      search_performed: !!searchQuery,
      search_query: searchQuery || null
    };

  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('❌ API dataset function execution failed:', error);
    
    return {
      success: false,
      error: true,
      message: `API call failed: ${error.response?.data?.message || error.message}`,
      status: error.response?.status,
      function_name: functionName,
      dataset_id: datasetId
    };
  }
};

// Get combined available functions (static + dynamic)
export const getAvailableFunctions = async () => {
  const dynamicFunctions = await loadApiDatasetFunctions();
  return [...staticAvailableFunctions, ...dynamicFunctions];
};

// Legacy export for backward compatibility
export const availableFunctions = staticAvailableFunctions;

// Weather function implementation using open-meteo API
const getWeather = async (latitude, longitude, location_name = "Unknown location") => {
  process.env.NODE_ENV === "development" && console.log(`🌤️ Getting weather for ${location_name} (${latitude}, ${longitude})`);
  
  try {
    const response = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,wind_speed_10m,weather_code,relative_humidity_2m&hourly=temperature_2m,relative_humidity_2m,wind_speed_10m&timezone=auto`
    );
    
    if (!response.ok) {
      throw new Error(`Weather API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    const current = data.current;
    const hourly = data.hourly;
    
    return {
      success: true,
      location: location_name,
      coordinates: { latitude, longitude },
      current_weather: {
        temperature: `${current.temperature_2m}°C`,
        wind_speed: `${current.wind_speed_10m} km/h`,
        humidity: `${current.relative_humidity_2m}%`,
        time: current.time
      },
      forecast: {
        next_hours: hourly.time.slice(0, 6).map((time, index) => ({
          time: time,
          temperature: `${hourly.temperature_2m[index]}°C`,
          humidity: `${hourly.relative_humidity_2m[index]}%`,
          wind_speed: `${hourly.wind_speed_10m[index]} km/h`
        }))
      },
      data_source: "Open-Meteo API",
      timezone: data.timezone,
      sources: [{
        url: "https://open-meteo.com/",
        title: "Open-Meteo Weather API",
        type: "api",
        description: "Free weather API for non-commercial use"
      }]
    };
    
  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('❌ Error getting weather:', error);
    return {
      success: false,
      error: true,
      message: `Failed to get weather for ${location_name}: ${error.message}`,
      coordinates: { latitude, longitude }
    };
  }
};

// Helper function to parse NCBI XML and extract abstracts
const parseNCBIXML = (xmlText, articleIds) => {
  const results = {};
  
  try {
    // Create a simple XML parser using DOMParser
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, "text/xml");
    
    // Find all article elements
    const articles = xmlDoc.querySelectorAll('article');
    process.env.NODE_ENV === "development" && console.log(`📄 Found ${articles.length} articles in XML, expecting ${articleIds.length} article IDs`);
    
    articles.forEach((article, index) => {
      process.env.NODE_ENV === "development" && console.log(`📄 Processing article at index ${index}`);
      
      // Try to extract the actual PMC ID from the article element
      let articleId = null;
      
      // Method 1: Look for PMC ID in article-id elements (more specific selectors)
      const articleIdElements = article.querySelectorAll('article-id[pub-id-type="pmc"], front article-meta article-id[pub-id-type="pmc"], front-stub article-id[pub-id-type="pmc"]');
      if (articleIdElements.length > 0) {
        articleId = articleIdElements[0].textContent?.trim();
        process.env.NODE_ENV === "development" && console.log(`📄 Found PMC ID using method 1: ${articleId}`);
      }
      
      // Method 2: Look for any article-id elements and log them for debugging
      if (!articleId) {
        const allIdElements = article.querySelectorAll('article-id');
        process.env.NODE_ENV === "development" && console.log(`📄 Found ${allIdElements.length} article-id elements for debugging:`);
        allIdElements.forEach((idEl, i) => {
          const idType = idEl.getAttribute('pub-id-type');
          const idValue = idEl.textContent?.trim();
          process.env.NODE_ENV === "development" && console.log(`📄   ID ${i}: type="${idType}", value="${idValue}"`);
          
          // If we find pmc type, use it
          if (idType === 'pmc' && idValue) {
            articleId = idValue;
            process.env.NODE_ENV === "development" && console.log(`📄 Found PMC ID using method 2: ${articleId}`);
          }
        });
      }
      
      // Method 3: Look for PMC ID in processing-meta or other locations
      if (!articleId) {
        const processingMeta = article.querySelector('processing-meta');
        if (processingMeta) {
          process.env.NODE_ENV === "development" && console.log(`📄 Found processing-meta, checking for PMC ID`);
          // Try to find PMC ID in processing meta
          const pmcElements = processingMeta.querySelectorAll('*[*|id], *[id]');
          pmcElements.forEach(el => {
            const id = el.getAttribute('id') || el.getAttribute('xml:id');
            if (id && id.includes('PMC')) {
              articleId = id.replace('PMC', '');
              process.env.NODE_ENV === "development" && console.log(`📄 Found PMC ID in processing-meta: ${articleId}`);
            }
          });
        }
      }
      
      // Method 4: Extract from URL if present
      if (!articleId) {
        const links = article.querySelectorAll('a[href*="PMC"], uri[*|href*="PMC"]');
        if (links.length > 0) {
          const href = links[0].getAttribute('href') || links[0].getAttribute('xlink:href');
          const pmcMatch = href.match(/PMC(\d+)/);
          if (pmcMatch) {
            articleId = pmcMatch[1];
            process.env.NODE_ENV === "development" && console.log(`📄 Found PMC ID from URL: ${articleId}`);
          }
        }
      }
      
      // If we still don't have an ID, let's try to find it in the entire article content
      if (!articleId) {
        const articleText = article.innerHTML || article.textContent || '';
        const pmcMatches = articleText.match(/PMC(\d+)/g);
        if (pmcMatches && pmcMatches.length > 0) {
          // Use the first PMC ID found
          articleId = pmcMatches[0].replace('PMC', '');
          process.env.NODE_ENV === "development" && console.log(`📄 Found PMC ID in article content: ${articleId}`);
        }
      }
      
      // Fallback: Use array index if we can't find the PMC ID
      if (!articleId && index < articleIds.length) {
        articleId = articleIds[index];
        process.env.NODE_ENV === "development" && console.warn(`⚠️ Could not find PMC ID in article ${index}, using fallback: ${articleId}`);
      }
      
      if (!articleId) {
        process.env.NODE_ENV === "development" && console.warn(`⚠️ Could not determine article ID for article at index ${index}`);
        return;
      }
      
      // Ensure we use just the numeric ID (without PMC prefix) for consistency
      if (articleId.startsWith('PMC')) {
        articleId = articleId.replace('PMC', '');
        process.env.NODE_ENV === "development" && console.log(`📄 Removed PMC prefix, using numeric ID: ${articleId}`);
      }
      
      process.env.NODE_ENV === "development" && console.log(`📄 Processing article with ID: ${articleId}`);
      
      let abstract = '';
      let fullText = '';
      
      // Try to find abstract in various possible locations
      const abstractElements = article.querySelectorAll('abstract p, abstract sec p, abstract, front article-meta abstract p, front article-meta abstract');
      process.env.NODE_ENV === "development" && console.log(`📄 Found ${abstractElements.length} potential abstract elements`);
      
      abstractElements.forEach((elem, i) => {
        const text = elem.textContent?.trim();
        if (text && text.length > 20) { // Filter out very short text
          process.env.NODE_ENV === "development" && console.log(`📄   Abstract element ${i}: ${text.length} chars`);
          abstract += text + ' ';
        }
      });
      
      // Also try to get some body text for context
      const bodyElements = article.querySelectorAll('body p, body sec p');
      process.env.NODE_ENV === "development" && console.log(`📄 Found ${bodyElements.length} potential body elements`);
      
      let bodyText = '';
      for (let i = 0; i < Math.min(bodyElements.length, 3); i++) {
        const text = bodyElements[i].textContent?.trim();
        if (text && text.length > 20) {
          bodyText += text + ' ';
        }
      }
      
      // Clean up abstract
      abstract = abstract.trim();
      fullText = bodyText.trim();
      
      if (abstract || fullText) {
        results[articleId] = {
          abstract: abstract || 'Abstract not available',
          fullText: fullText.substring(0, 1000) // Limit full text to 1000 chars
        };
        process.env.NODE_ENV === "development" && console.log(`✅ Successfully extracted content for article ${articleId} (abstract: ${abstract.length} chars, fullText: ${fullText.length} chars)`);
      } else {
        process.env.NODE_ENV === "development" && console.warn(`⚠️ No content extracted for article ${articleId}`);
      }
    });
    
  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('Error parsing NCBI XML:', error);
  }
  
  process.env.NODE_ENV === "development" && console.log(`📄 Final results object:`, Object.keys(results));
  return results;
};

// Helper function for CORS-aware fetch with proxy fallback
const corsAwareFetch = async (url) => {
  try {
    // Try direct fetch first
    process.env.NODE_ENV === "development" && console.log(`📡 Trying direct fetch: ${url}`);
    const response = await fetch(url);
    process.env.NODE_ENV === "development" && console.log(`✅ Direct fetch successful: ${response.status}`);
    return response;
  } catch (directError) {
    // Check if it's a CORS error
    const isCorsError = (
      directError.message.includes('CORS') ||
      directError.message.includes('Network Error') ||
      directError.message.includes('Failed to fetch')
    );
    
    if (isCorsError) {
      process.env.NODE_ENV === "development" && console.log(`🔄 Direct fetch failed with CORS, trying proxy...`);
      try {
        // Use proxy for CORS bypass
        const proxyUrl = `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(url)}`;
        process.env.NODE_ENV === "development" && console.log(`📡 Using proxy: ${proxyUrl}`);
        
        const proxyResponse = await axios.get(proxyUrl, {
          timeout: 30000,
          headers: {
            'Accept': 'application/json, text/plain, */*'
          }
        });
        
        process.env.NODE_ENV === "development" && console.log(`✅ Proxy fetch successful: ${proxyResponse.status}`);
        
        // Convert axios response to fetch-like response
        return {
          ok: proxyResponse.status >= 200 && proxyResponse.status < 300,
          status: proxyResponse.status,
          json: () => Promise.resolve(proxyResponse.data),
          text: () => Promise.resolve(typeof proxyResponse.data === 'string' ? proxyResponse.data : JSON.stringify(proxyResponse.data))
        };
      } catch (proxyError) {
        process.env.NODE_ENV === "development" && console.error(`❌ Proxy fetch also failed:`, proxyError);
        throw directError; // Throw original error
      }
    } else {
      throw directError; // Re-throw if not CORS
    }
  }
};

// NCBI PubMed Central search function implementation
const searchNCBI = async (query, max_results = 5, search_field = "all") => {
  process.env.NODE_ENV === "development" && console.log(`🔬 Searching NCBI for: "${query}" (max: ${max_results}, field: ${search_field})`);
  
  try {
    // Limit max_results to prevent API abuse
    const safeMaxResults = Math.min(max_results, 20);
    
    // Build search term based on field
    let searchTerm = query;
    if (search_field === "title") {
      searchTerm = `${query}[Title]`;
    } else if (search_field === "abstract") {
      searchTerm = `${query}[Abstract]`;
    } else if (search_field === "author") {
      searchTerm = `${query}[Author]`;
    }
    
    process.env.NODE_ENV === "development" && console.log(`🔍 Final search term: "${searchTerm}"`);
    
    // Step 1: Search for article IDs
    const searchUrl = `https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esearch.fcgi?db=pmc&term=${encodeURIComponent(searchTerm)}&retmax=${safeMaxResults}&retmode=json`;
    
    process.env.NODE_ENV === "development" && console.log(`📡 NCBI Search URL: ${searchUrl}`);
    
    const searchResponse = await corsAwareFetch(searchUrl);
    process.env.NODE_ENV === "development" && console.log(`📊 Search response status: ${searchResponse.status}`);
    
    if (!searchResponse.ok) {
      throw new Error(`NCBI search failed: ${searchResponse.status}`);
    }
    
    const searchData = await searchResponse.json();
    process.env.NODE_ENV === "development" && console.log(`📋 Search data received:`, searchData);
    
    const articleIds = searchData.esearchresult?.idlist || [];
    process.env.NODE_ENV === "development" && console.log(`📝 Article IDs found: ${articleIds.length}`, articleIds);
    
    if (articleIds.length === 0) {
      process.env.NODE_ENV === "development" && console.log(`❌ No articles found for query: "${query}"`);
      return {
        success: true,
        query: query,
        total_found: 0,
        articles: [],
        message: "No articles found for your search query"
      };
    }
    
    // Step 2: Get article summaries
    const summaryUrl = `https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esummary.fcgi?db=pmc&id=${articleIds.join(',')}&retmode=json`;
    
    const summaryResponse = await corsAwareFetch(summaryUrl);
    if (!summaryResponse.ok) {
      throw new Error(`NCBI summary failed: ${summaryResponse.status}`);
    }
    
    const summaryData = await summaryResponse.json();
    
    // Step 3: Get article abstracts using efetch
    const fetchUrl = `https://eutils.ncbi.nlm.nih.gov/entrez/eutils/efetch.fcgi?db=pmc&id=${articleIds.join(',')}&retmode=xml`;
    
    process.env.NODE_ENV === "development" && console.log(`📄 NCBI Fetch URL: ${fetchUrl}`);
    
    let articlesWithAbstracts = {};
    try {
      const fetchResponse = await corsAwareFetch(fetchUrl);
      process.env.NODE_ENV === "development" && console.log(`📄 Fetch response status: ${fetchResponse.status}`);
      
      if (fetchResponse.ok) {
        const xmlText = await fetchResponse.text();
        process.env.NODE_ENV === "development" && console.log(`📄 XML text length: ${xmlText.length}`);
        process.env.NODE_ENV === "development" && console.log(`📄 XML preview: ${xmlText.substring(0, 500)}...`);
        
        // Parse XML to extract abstracts
        articlesWithAbstracts = parseNCBIXML(xmlText, articleIds);
        process.env.NODE_ENV === "development" && console.log(`📄 Abstracts parsed for articles:`, Object.keys(articlesWithAbstracts));
      } else {
        process.env.NODE_ENV === "development" && console.warn('Failed to fetch abstracts, continuing with basic data');
      }
    } catch (fetchError) {
      process.env.NODE_ENV === "development" && console.warn('Error fetching abstracts:', fetchError);
    }
    
    const articles = [];
    
    // Parse article data
    for (const id of articleIds) {
      const article = summaryData.result?.[id];
      if (article) {
        const articleData = {
          pmcid: `PMC${id}`,
          title: article.title || "No title available",
          authors: article.authors?.map(a => a.name)?.join(", ") || "Unknown authors",
          journal: article.fulljournalname || article.source || "Unknown journal",
          pub_date: article.pubdate || "Unknown date",
          doi: article.elocationid || null,
          abstract_url: `https://www.ncbi.nlm.nih.gov/pmc/articles/PMC${id}/`,
          summary: article.title // Basic summary - could be enhanced
        };
        
        // Add abstract if available - check multiple possible ID formats
        process.env.NODE_ENV === "development" && console.log(`🔍 Looking for abstract data for article ID: ${id}`);
        process.env.NODE_ENV === "development" && console.log(`🔍 Available abstract keys:`, Object.keys(articlesWithAbstracts));
        
        let abstractData = null;
        
        // Try different ID formats
        if (articlesWithAbstracts[id]) {
          abstractData = articlesWithAbstracts[id];
          process.env.NODE_ENV === "development" && console.log(`✅ Found abstract data using format: ${id}`);
        } else if (articlesWithAbstracts[`PMC${id}`]) {
          abstractData = articlesWithAbstracts[`PMC${id}`];
          process.env.NODE_ENV === "development" && console.log(`✅ Found abstract data using format: PMC${id}`);
        } else if (articlesWithAbstracts[id.toString()]) {
          abstractData = articlesWithAbstracts[id.toString()];
          process.env.NODE_ENV === "development" && console.log(`✅ Found abstract data using format: ${id.toString()}`);
        } else {
          process.env.NODE_ENV === "development" && console.warn(`⚠️ No abstract data found for article ${id} in any format`);
        }
        
        if (abstractData) {
          articleData.abstract = abstractData.abstract;
          articleData.fullText = abstractData.fullText;
          process.env.NODE_ENV === "development" && console.log(`📄 Added abstract (${abstractData.abstract?.length} chars) and fullText (${abstractData.fullText?.length} chars) to article ${id}`);
        }
        
        articles.push(articleData);
      }
    }
    
    return {
      success: true,
      query: query,
      search_field: search_field,
      total_found: parseInt(searchData.esearchresult?.count || 0),
      results_returned: articles.length,
      articles: articles.map(article => ({
        ...article,
        sources: [{
          url: article.abstract_url,
          title: article.title,
          type: "scientific_article",
          journal: article.journal
        }]
      })),
      data_source: "NCBI PubMed Central",
      search_url: searchUrl,
      sources: [{
        url: "https://www.ncbi.nlm.nih.gov/pmc/",
        title: "NCBI PubMed Central",
        type: "database",
        description: "Free full-text archive of biomedical and life sciences journal literature"
      }]
    };
    
  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('❌ Error searching NCBI:', error);
    return {
      success: false,
      error: true,
      message: `Failed to search NCBI: ${error.message}`,
      query: query
    };
  }
};

// Function executor - maps function names to implementations
export const executeFunction = async (functionCall) => {
  const { name, arguments: args } = functionCall;
  
  process.env.NODE_ENV === "development" && console.log(`🚀 EXECUTE FUNCTION START`);
  process.env.NODE_ENV === "development" && console.log(`📋 Function name: ${name}`);
  process.env.NODE_ENV === "development" && console.log(`📋 Function arguments:`, args);
  
  try {
    const params = JSON.parse(args);
    process.env.NODE_ENV === "development" && console.log(`📋 Parsed parameters:`, params);
    
    // Check static functions first
    process.env.NODE_ENV === "development" && console.log(`🔍 Checking static functions...`);
    switch (name) {
      case 'get_weather':
        process.env.NODE_ENV === 'development' && console.log('Executing static weather function');
        return await getWeather(params.latitude, params.longitude, params.location_name);

      case 'search_ncbi_articles':
        process.env.NODE_ENV === 'development' && console.log('Executing static NCBI search function');
        return await searchNCBI(params.query, params.max_results, params.search_field);

      default:
        break;
    }

    // Check dynamic API dataset functions
    process.env.NODE_ENV === "development" && console.log(`🔍 Checking dynamic API dataset functions...`);
    const dynamicFunctions = await loadApiDatasetFunctions();
    process.env.NODE_ENV === "development" && console.log(`📊 Available dynamic functions:`, dynamicFunctions.map(f => f.name));
    
    const dynamicFunction = dynamicFunctions.find(func => func.name === name);
    
    if (dynamicFunction) {
      process.env.NODE_ENV === "development" && console.log(`🎯 FOUND DYNAMIC FUNCTION: ${name}`);
      process.env.NODE_ENV === "development" && console.log(`📋 Dataset ID: ${dynamicFunction.datasetId}`);
      process.env.NODE_ENV === "development" && console.log(`📋 Function definition:`, dynamicFunction);
      
      const result = await executeApiDatasetFunction(name, params, dynamicFunction.datasetId);
      process.env.NODE_ENV === "development" && console.log(`✅ Dynamic function execution completed:`, result);
      return result;
    }
    
    // Function not found
    process.env.NODE_ENV === "development" && console.error(`❌ FUNCTION NOT FOUND: ${name}`);
    process.env.NODE_ENV === "development" && console.log(`📋 Available static functions: get_weather, search_ncbi_articles`);
    process.env.NODE_ENV === "development" && console.log(`📋 Available dynamic functions:`, dynamicFunctions.map(f => f.name));
    
    return {
      error: true,
      message: `Function "${name}" is not implemented`
    };
    
  } catch (error) {
    process.env.NODE_ENV === "development" && console.error('❌ EXECUTE FUNCTION ERROR:', error);
    process.env.NODE_ENV === "development" && console.error('❌ Error stack:', error.stack);
    return {
      error: true,
      message: `Failed to execute function "${name}": ${error.message}`
    };
  }
};

// Helper to get tools array for OpenAI API
export const getToolsArray = async (includeFunctions = true) => {
  process.env.NODE_ENV === "development" && console.log('🛠️ getToolsArray called with includeFunctions:', includeFunctions);
  const tools = [{ "type": "file_search" }];
  
  if (includeFunctions) {
    // Add static functions
    staticAvailableFunctions.forEach(func => {
      tools.push({
        type: "function",
        function: func
      });
    });
    process.env.NODE_ENV === "development" && console.log(`🛠️ Added ${staticAvailableFunctions.length} static functions`);
    
    // Add dynamic API dataset functions
    try {
      process.env.NODE_ENV === "development" && console.log('🛠️ Loading dynamic API dataset functions...');
      const dynamicFunctions = await loadApiDatasetFunctions();
      process.env.NODE_ENV === "development" && console.log('🛠️ Received dynamic functions:', dynamicFunctions);
      
      dynamicFunctions.forEach((func, index) => {
        process.env.NODE_ENV === "development" && console.log(`🛠️ Adding dynamic function ${index + 1}:`, func.name);
        // Remove datasetId from the function definition sent to OpenAI
        const { datasetId, ...functionDef } = func;
        tools.push({
          type: "function",
          function: functionDef
        });
      });
      
      process.env.NODE_ENV === "development" && console.log(`🛠️ Tools array generated with ${staticAvailableFunctions.length} static + ${dynamicFunctions.length} dynamic functions`);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('❌ Error loading dynamic functions for tools array:', error);
      process.env.NODE_ENV === "development" && console.error('❌ Error stack:', error.stack);
      process.env.NODE_ENV === "development" && console.log(`🛠️ Tools array generated with ${staticAvailableFunctions.length} static functions only`);
    }
  }
  
  process.env.NODE_ENV === "development" && console.log(`🛠️ Final tools array:`, tools);
  return tools;
}; 